import { useCallback, useMemo, useEffect, useRef } from "react";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import Ribbon, { RibbonItem } from "@components/Ribbon/Ribbon";
import LazyImage from "@components/LazyImage/LazyImage";
import placeholderImg from "../../../../img/image-placeholder.png";
import { components } from "@apiSchema";
import dayjs from "dayjs";
import cn from "classnames";
import {
    RIBBON_DEFAULTS,
    buildNewsArticleUrl,
} from "@pages/Home/sections/HomeReportsAndNews/HomeReportsAndNews.helpers";

const NewsBadge = ({ label }: { label: string }) => (
    <span className="HomeNews__badge">
        {label}
    </span>
);

const HomeNews: FCX<{
    data: components["schemas"]["RecentNewsArticleModel"][];
    setBgSrc: (src: string) => void;
    onNavReady?: (handlers: { next: () => void; prev: () => void }) => void;
}> = ({
    data: rawData,
    setBgSrc,
    onNavReady,
}) => {
    const {
        userInfo: {
            products,
        },
    } = usePlatformContext();
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pinnedImageUrl = useMemo(
        () => rawData?.[0]?.featuredImage ?? '',
        [rawData]
    );

    useEffect(() => {
        setBgSrc(pinnedImageUrl);
    }, [pinnedImageUrl, setBgSrc]);

    // Preload all news card images upfront
    useEffect(() => {
        rawData?.forEach(item => {
            if (item.featuredImage) {
                const img = new Image();
                img.src = item.featuredImage;
            }
        });
    }, [rawData]);

    const handleCardHover = useCallback((imageUrl: string) => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setBgSrc(imageUrl || pinnedImageUrl);
        }, 100);
    }, [setBgSrc, pinnedImageUrl]);

    const handleCardLeave = useCallback(() => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setBgSrc(pinnedImageUrl);
        }, 100);
    }, [setBgSrc, pinnedImageUrl]);

    const reportProProduct = useMemo(
        () => products[AppId.ReportPro],
        [products]
    );

    const limitedData = useMemo(() => rawData ?? [], [rawData]);

    const data = useMemo(
        (): RibbonItem[] => limitedData.map(i => ({
            id: i.id ?? 0,
            title: i.title ?? '',
            description: dayjs(i.publishedDate ?? '').format('MM/DD/YYYY'),
            url: buildNewsArticleUrl(reportProProduct.url, i),
            imageUrl: i.featuredImage ?? '',
        })) ?? [],
        [limitedData, reportProProduct]
    );

    const renderBadge = useCallback((_item: RibbonItem, index: number) => {
        if (index === 0) {
            return <NewsBadge label="Latest Daily Dish" />;
        }
        return null;
    }, []);

    const renderItem = useCallback((item: RibbonItem, index: number, isActive: boolean) => {
        return (
            <a
                href={item.url ?? ""}
                target="_blank"
                rel="noreferrer"
                key={item.id}
                className={cn("Ribbon__item", { "is-active": isActive })}
                onMouseEnter={() => handleCardHover(item.imageUrl ?? '')}
                onMouseLeave={handleCardLeave}
            >
                <div className="Ribbon__item-image-sizer">
                    <LazyImage
                        src={item.imageUrl ?? ''}
                        alt={item.title}
                        placeholder={<img src={placeholderImg as string} alt="" />}
                    />
                    {renderBadge && (
                        <div className="Ribbon__item-badge">
                            {renderBadge(item, index)}
                        </div>
                    )}
                </div>
                <div className="Ribbon__item-content">
                    <div className="Ribbon__item-fallback">
                        <div className="Ribbon__item-fallback-title">{item.title}</div>
                        <div className="Ribbon__item-fallback-description">{item.description}</div>
                    </div>
                </div>
            </a>
        );
    }, [handleCardHover, handleCardLeave, renderBadge]);

    return (
        <Ribbon
            {...RIBBON_DEFAULTS}
            maxSlidesPerView={6}
            minTileWidth={150}
            items={data}
            isAutoSlideDisabled
            className="appears-right"
            itemVariations={['text-always', 'landscape']}
            pinnedFirstTile
            hideBuiltInNav
            onNavReady={onNavReady}
            renderBadge={renderBadge}
            renderItem={renderItem}
        />
    );
}

export default HomeNews;
